// Cliente del streaming SSE de POST /chat del sidecar de IA (Fase 4 del plan
// de integración — docs/PLAN_MCP_SERVER.md). Se usa fetch con reader (axios
// no streamea respuestas en browser). Contrato de eventos (definido en
// cforemoto-ai/src/cforemoto_ai/chat.py): delta | tool | done | error.

// Parser incremental de frames SSE: procesa los frames completos del buffer
// y devuelve el resto (un frame puede llegar cortado entre chunks).
export function parseSseBuffer(buffer) {
	const events = [];
	let rest = buffer;
	let index = rest.indexOf("\n\n");
	while (index >= 0) {
		const frame = rest.slice(0, index);
		rest = rest.slice(index + 2);
		let event = "message";
		let data = "";
		for (const line of frame.split("\n")) {
			if (line.startsWith("event: ")) {
				event = line.slice(7);
			} else if (line.startsWith("data: ")) {
				data += line.slice(6);
			}
		}
		if (data) {
			events.push({ event, data: JSON.parse(data) });
		}
		index = rest.indexOf("\n\n");
	}
	return { events, rest };
}

// Mensajes mostrables — nunca el detalle técnico (invariante del plan: el
// widget no expone internals).
const CONNECT_ERROR = "No pudimos conectar con el asistente.";
const SESSION_ERROR = "Tu sesión expiró. Recarga la página e intenta de nuevo.";
const GENERIC_ERROR = "El asistente no está disponible en este momento.";

// Cupo agotado (fase 2d): el 429 del sidecar trae `quota: daily|weekly`
// (contrato de la 3b). Los cortes son calendario America/Santiago, por eso
// "mañana" / "el lunes" son exactos.
const QUOTA_MESSAGES = {
	daily: "Alcanzaste tu límite diario de Phi. Se renueva mañana.",
	weekly: "Alcanzaste tu límite semanal de Phi. Se renueva el lunes.",
};
const QUOTA_FALLBACK = "Alcanzaste tu límite de uso de Phi.";

/**
 * Streamea una conversación contra el sidecar. Los handlers reciben el
 * contenido ya parseado; ante error se llama onError UNA vez y se corta.
 * `fetchFn` inyectable para los specs.
 */
export async function streamChat({
	url,
	token,
	activeCompany,
	message,
	history,
	onDelta,
	onTool,
	onDone,
	onError,
	fetchFn = (...args) => window.fetch(...args),
}) {
	let response;
	try {
		response = await fetchFn(`${url}/chat`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
				...(activeCompany ? { "X-Active-Company": activeCompany } : {}),
			},
			body: JSON.stringify({ message, history }),
		});
	} catch {
		onError(CONNECT_ERROR);
		return;
	}
	if (response.status === 401) {
		onError(SESSION_ERROR);
		return;
	}
	if (response.status === 429) {
		// Estado propio, no error genérico: el widget lo muestra sin botón
		// de reintento (reintentar ahora no puede funcionar).
		let body = null;
		try {
			body = await response.json();
		} catch {
			// Sin body parseable: mensaje de cupo genérico.
		}
		onError(QUOTA_MESSAGES[body?.quota] || QUOTA_FALLBACK, { quota: true });
		return;
	}
	if (!response.ok || !response.body) {
		onError(GENERIC_ERROR);
		return;
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	for (;;) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}
		buffer += decoder.decode(value, { stream: true });
		const parsed = parseSseBuffer(buffer);
		buffer = parsed.rest;
		for (const { event, data } of parsed.events) {
			if (event === "delta") {
				onDelta(data.text);
			} else if (event === "tool") {
				onTool(data.name);
			} else if (event === "error") {
				onError(data.error || GENERIC_ERROR);
				return;
			} else if (event === "done") {
				onDone();
				return;
			}
		}
	}
	// El stream cerró sin frame `done` (corte de red): cerrar prolijo igual.
	onDone();
}
